<template>
  <div v-observe ref="rootEl" class="plans-broadband" :class="{ 'no-term': !hasTerm }">
    <div v-if="hasTerm" class="plans-broadband__toggle">
      <term-toggle :term="term" />
    </div>
    <div class="plans-broadband__content">
      <template v-if="hyperOnly">
        <plan-card v-for="(plan, index) in plans" :key="index" :plan="plan" :hyper-cta="false" :promo="promo" @selected="onPlanSelected" />
      </template>
      <template v-else>
        <plan-card
          v-for="(plan, index) in plans"
          :key="index"
          :plan="plan"
          hyper-cta-modifier="outlined"
          :hyper-cta="hyperCta"
          :promo="promo"
          :old-price-no-term="oldPriceNoTerm"
          @selected="onPlanSelected"
        />
      </template>
      <slot name="afterPlans" />
      <template v-if="hyperOnly">
        <div class="plans-broadband__features">
          <hyper-plans-features-icon />
        </div>
      </template>
      <plans-note>
        <template v-if="promo === FNF_PROMO_CODE" #header>
          <strong>*TERMS</strong>
        </template>

        <template v-if="hyperOnly || promo === FNF_PROMO_CODE" #content>
          <p v-if="hyperOnly">There are no independent reports on Hyperfibre plan speeds. You may experience higher or lower speeds than this.</p>
          <template v-if="promo === FNF_PROMO_CODE">
            <ul>
              <li><strong>Friends & Family Offer:</strong> Receive 20% off any Zeronet broadband plan of your choice.</li>
              <li>
                <strong>Bundle Deal Offer:</strong>
                <ul>
                  <li>Add Zeronet Mobile to your broadband plan and save $5/month on broadband.</li>

                  <li>You can add up to 5 SIMs, increasing your broadband savings by up to $25/month.</li>
                </ul>
              </li>
            </ul>
          </template>
        </template>

        <template #after>
          <br />

          <template v-if="promo == '5month'">
            <p><strong>*OFFERS:</strong></p>
            <ul>
              <li>Half-Price Duration: Fibre Starter (5 months), Fibre Essentials (5 months)</li>
              <li>Post-Offer Prices: Fibre Starter ($67/m), Fibre Essentials ($100/m)</li>
            </ul>
            <br />
            <p><strong>*TERMS:</strong></p>
            <p>12-month contract. Early termination and setup fees apply.</p>
          </template>
          <template v-else-if="promo == '6month'">
            <p><strong>*OFFERS:</strong></p>
            <ul>
              <li>
                Half-Price Duration: Fibre Starter (6 months), Fibre Essentials (6 months), Fibre Max (6 months), Hyperfibre 2000 (6 months),
                Hyperfibre 4000 (6 months)
              </li>
              <li>
                Post-Offer Prices: Fibre Starter ($67/m), Fibre Essentials ($100/m), Fibre Max ($115/m), Hyperfibre 2000 ($135/m), Hyperfibre 4000
                ($165/m)
              </li>
            </ul>
            <br />
            <p><strong>*TERMS:</strong></p>
            <p>12-month contract. Early termination and setup fees apply.</p>
          </template>
          <template v-else-if="['compass'].includes(promo)">
            <p><strong>*OFFERS:</strong></p>
            <ul>
              <li>Half-Price Duration: Fibre Starter (6 months), Fibre Essentials (6 months), Fibre Max (6 months)</li>
              <li>Post-Offer Prices: Fibre Starter ($67/m), Fibre Essentials ($100/m), Fibre Max ($115/m)</li>
            </ul>
            <br />
            <p><strong>*TERMS:</strong></p>
            <p>12-month contract. Early termination and setup fees apply.</p>
          </template>
          <template v-else-if="promo !== FNF_PROMO_CODE">
            <p><strong>*OFFERS:</strong></p>
            <ul>
              <li>
                Half-Price Duration: Fibre Starter (2 months), Fibre Essentials (3 months), Fibre Max (4 months), Hyperfibre 2000 (5 months),
                Hyperfibre 4000 (6 months)
              </li>
              <li>
                Post-Offer Prices: Fibre Starter ($67/m), Fibre Essentials ($100/m), Fibre Max ($115/m), Hyperfibre 2000 ($135/m), Hyperfibre 4000
                ($165/m)
              </li>
            </ul>
            <br />
            <p><strong>*TERMS:</strong></p>
            <p>12-month contract. Early termination and setup fees apply.</p>
          </template>
        </template>
      </plans-note>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, useRouter, useTracking, watch } from '#imports';
import { useProducts } from '@/stores/products';
import PlanCard from '@/components/global/products/PlanCard.vue';
import type { Plan } from '@/types/products';
import { useMemberSignup } from '@/stores/signup';
import sortBy from 'lodash/sortBy';
import TermToggle from '~/components/global/products/TermToggle.vue';
import PlansNote from '~/components/local/plans/PlansNote.vue';
import HyperPlansFeaturesIcon from '~/components/local/plans/HyperPlansFeaturesIcon.vue';
import { FNF_PROMO_CODE } from '~/lib/constants';
import { useElementVisibility } from '@vueuse/core';

const props = defineProps({
  hyper: {
    type: Boolean,
    default: false,
  },
  hyperOnly: {
    type: Boolean,
    default: false,
  },
  hyperCta: {
    type: Boolean,
    default: true,
  },
  noHyper: {
    type: Boolean,
    default: false,
  },
  offer: {
    type: Number,
    default: null,
  },
  term: {
    type: Number,
    default: 1,
  },
  hasTerm: {
    type: Boolean,
    default: true,
  },
  promo: {
    type: String,
    default: 'signup',
  },
  oldPriceNoTerm: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const products = useProducts();
const signup = useMemberSignup();

const { trackViewContent, trackInitiateCheckout } = useTracking();
const rootEl = useTemplateRef('rootEl');
const targetIsVisible = useElementVisibility(rootEl);
const hasTriggered = ref(false);
watch(
  targetIsVisible,
  (val: any) => {
    if (val && !hasTriggered.value) {
      trackViewContent({
        content_name: 'Plans',
        content_category: 'Broadband',
        content_promo: props.promo || '-',
      });
      hasTriggered.value = true;
    }
  },
  {
    immediate: true,
  }
);

const plans = computed(() => {
  if (props.promo === '6month') {
    return products.plans.filter((plan) => plan.id !== 1);
  }

  if (props.promo === '5month') {
    return products.plans.filter((plan) => [1, 2].includes(plan.id as number));
  }

  if (props.hyperOnly) {
    return products.plans.filter((plan) => plan.hyper);
  }
  if (props.noHyper) {
    return products.plans.filter((plan) => !plan.hyper);
  }
  if (props.hyper) {
    return sortBy(products.plans, (prod) => prod.hyper);
  }
  return products.plans;
});

const onPlanSelected = (plan: Plan) => {
  signup.saveDetails({
    plan,
    fromLanding: true,
    promo: props.promo,
  });

  trackInitiateCheckout({
    content_name: 'Plans',
    content_category: 'Broadband',
    content_promo: props.promo || '-',
    content_plan: plan.name,
  });

  router.push('/register/1');
};
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/assets/mixins/__index' as mix;

.plans-broadband {
  z-index: 9;
  position: relative;
  margin-left: auto;
  margin-right: auto;
  //
  //&.no-term {
  //  padding-top: 2.5em;
  //}

  @include desktop {
    transition-delay: 0.6s;
    max-width: rem(900);
  }

  &__toggle {
    margin-bottom: rem(24);
    margin-left: auto;
    margin-right: auto;
    max-width: rem(300);
    @include tablet {
      margin-bottom: rem(40);
      max-width: rem(360);
    }
  }

  &__content {
    position: relative;
    display: grid;
    gap: var(--container-spacing);
    @include desktop {
      gap: rem(40);
    }
  }

  &__features {
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      display: block;
      width: 100%;
      max-width: rem(360);
      height: auto;
    }
  }
}
</style>
